import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTable } from 'react-table';
import './AdminComponent.css';

const MONITORED_IPS = [
  '100.14.228.30',  
  '100.14.229.75',  
  '100.11.189.110',
  '73.233.233.159',
  '69.137.129.69',   
  '107.77.192.169',   
  '2600:4040:74a2:d300:60d5:327d:9289:1330', 
  '2a02:8429:189:9801:6dcb:7b9e:a28c:c4a7',
  '2600:4040:74a2:d300:64f2:62c9:81d3:a97a'
];

// Bot detection patterns
const BOT_PATTERNS = {
  userAgents: [
    'bot', 'crawler', 'spider', 'slurp', 'search', 'mediapartners', 'nagios',
    'curl', 'wget', 'python-requests', 'java-http-client', 'apache-httpclient',
    'facebookexternalhit', 'twitterbot', 'rogerbot', 'linkedinbot', 'embedly',
    'quora link preview', 'showyoubot', 'outbrain', 'pinterest', 'slackbot',
    'vkShare', 'W3C_Validator', 'WordPress', 'wp-', 'feed', 'rss'
  ],
  paths: [
    '/wp-', '/xmlrpc.php', '/feed/', '/robots.txt', '/sitemap', '/.well-known',
    '/wlwmanifest.xml', '/license.txt', '/security.txt', '/humans.txt'
  ]
};

function isBotTraffic(user) {
  // Check user agent
  const userAgent = user.userAgent?.toLowerCase() || '';
  if (BOT_PATTERNS.userAgents.some(pattern => userAgent.includes(pattern))) {
    return true;
  }

  // Check referrer
  const referrer = user.referrer?.toLowerCase() || '';
  if (BOT_PATTERNS.paths.some(pattern => referrer.includes(pattern))) {
    return true;
  }

  // Check if they created a recipe (real users are more likely to interact)
  if (user.hasCreatedRecipe) {
    return false;
  }

  // Check if they have a normal browser user agent
  const hasNormalBrowser = /mozilla|chrome|safari|firefox|edge|opera/i.test(userAgent);
  if (!hasNormalBrowser) {
    return true;
  }

  return false;
}

function AdminPanel() {
  const [analyticsData, setAnalyticsData] = useState({
    users: [],
    analytics: {
      totalVisits: 0,
      recipeCreations: 0,
      referrers: {}
    }
  });
  const [useProductionData, setUseProductionData] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchAnalytics = async (useProd = false) => {
    setLoading(true);
    try {
      const endpoint = useProd ? '/api/production-analytics' : '/api/analytics';
      const response = await axios.get(endpoint);
      setAnalyticsData(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics(useProductionData);
    // Refresh data every 30 seconds
    const interval = setInterval(() => fetchAnalytics(useProductionData), 30000);
    return () => clearInterval(interval);
  }, [useProductionData]);

  const columnsUsers = [
    { Header: 'IP Address', accessor: 'ip' },
    { Header: 'Visit Time', accessor: 'timestamp' },
    { Header: 'Traffic Type', 
      accessor: row => isBotTraffic(row) ? 'Bot' : 'Real User',
      Cell: ({ value }) => (
        <span className={`traffic-type ${value.toLowerCase().replace(' ', '-')}`}>
          {value}
        </span>
      )
    },
    { Header: 'Referrer', accessor: 'referrer' },
    { Header: 'Created Recipe', accessor: 'hasCreatedRecipe' },
    { Header: 'User Agent', accessor: 'userAgent' }
  ];

  const Table = ({ data, columns }) => {
    const {
      getTableProps,
      getTableBodyProps,
      headerGroups,
      rows,
      prepareRow,
    } = useTable({ columns, data });

    return (
      <div className="table-container">
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <th {...column.getHeaderProps()}>{column.render('Header')}</th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map(row => {
              prepareRow(row);
              const isMonitored = MONITORED_IPS.includes(row.original.ip);
              return (
                <tr 
                  {...row.getRowProps()}
                  className={isMonitored ? 'monitored-ip' : ''}
                >
                  {row.cells.map(cell => (
                    <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const sortedUsers = [...analyticsData.users].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  return (
    <div className="admin-panel">
      <h1>Admin Dashboard</h1>
      
      <div className="data-source-toggle">
        <label>
          <input
            type="checkbox"
            checked={useProductionData}
            onChange={(e) => setUseProductionData(e.target.checked)}
          />
          Use Production Data
        </label>
        {loading && <span className="loading">Loading...</span>}
      </div>
      
      <div className="analytics-summary">
        <h2>Analytics Summary</h2>
        <div className="stats-grid">
          <div className="stat-card">
            <h3>Total Visits</h3>
            <p>{analyticsData.analytics.totalVisits}</p>
          </div>
          <div className="stat-card">
            <h3>Recipe Creations</h3>
            <p>{analyticsData.analytics.recipeCreations}</p>
          </div>
          <div className="stat-card">
            <h3>Conversion Rate</h3>
            <p>{analyticsData.analytics.totalVisits > 0 
              ? ((analyticsData.analytics.recipeCreations / analyticsData.analytics.totalVisits) * 100).toFixed(1) + '%'
              : '0%'}</p>
          </div>
        </div>
      </div>

      <div className="referrer-stats">
        <h2>Top Referrers</h2>
        <div className="stats-grid">
          {Object.entries(analyticsData.analytics.referrers)
            .sort(([,a], [,b]) => b - a)
            .slice(0, 5)
            .map(([referrer, count]) => (
              <div key={referrer} className="stat-card">
                <h3>{referrer || 'Direct'}</h3>
                <p>{count} visits</p>
              </div>
            ))}
        </div>
      </div>

      <div className="user-table">
        <h2>User Activity</h2>
        <Table data={sortedUsers} columns={columnsUsers} />
      </div>
    </div>
  );
}

export default AdminPanel;
